import { AssetType, BundleType, ConversionType, type Bundle, type ModuleTemplate } from "#gen/bundles/v1/bundle_pb.js";
import { SocialLinkType } from "#gen/bundles/v1/contact_pb.js";
import amex from "#lib/assets/icons/payment/amex.svg";
import dinersclub from "#lib/assets/icons/payment/diners.svg";
import discover from "#lib/assets/icons/payment/discover.svg";
import jcb from "#lib/assets/icons/payment/jcb.svg";
import mastercard from "#lib/assets/icons/payment/mastercard.svg";
import visa from "#lib/assets/icons/payment/visa.svg";
import applePay from "#lib/assets/icons/wallet/apple-pay.svg";
import googlePay from "#lib/assets/icons/wallet/google-pay.svg";
import shopPay from "#lib/assets/icons/wallet/shop-pay.svg";
import { parseIconUrl } from "#lib/icons.js";
import { Provider } from "#lib/provider.js";
import { insecureProtocol, mailtoProtocol, secureProtocol, smsProtocol, telProtocol } from "#lib/url.js";

export interface IconAsset {
	iconSet?: string;
	icon: string;
}

export const paymentIcons: Record<string, string> = {
	VISA: visa,
	MASTERCARD: mastercard,
	AMERICAN_EXPRESS: amex,
	DISCOVER: discover,
	DINERS_CLUB: dinersclub,
	JCB: jcb,
};

export const walletIcons: Record<string, string> = {
	SHOPIFY_PAY: shopPay,
	APPLE_PAY: applePay,
	GOOGLE_PAY: googlePay,
};

export const iconProvider: Map<BundleType, IconAsset> = new Map([
	[BundleType.REAL_ESTATE, { icon: "home", iconSet: "heroicons" }],
	[BundleType.COMMERCE, { icon: "building-storefront", iconSet: "heroicons" }],
	[BundleType.FORM, { icon: "window", iconSet: "heroicons" }],
	[BundleType.EXTERNAL, { icon: "link", iconSet: "heroicons" }],
	[BundleType.CONTACT, { icon: "identification", iconSet: "heroicons" }],
	[BundleType.CUSTOM, { icon: "list-bullet", iconSet: "heroicons" }],
]);

export const conversionIconProvider: Map<ConversionType, IconAsset> = new Map([
	[ConversionType.CONTACT_COLLECTION, { icon: "list-alt-add-outline", iconSet: "material-symbols" }],
	[ConversionType.PURCHASE, { icon: "building-storefront", iconSet: "heroicons" }],
	[ConversionType.FORM_SUBMISSION, { icon: "list-bullet", iconSet: "heroicons" }],
	[ConversionType.EXTERNAL_PIXEL, { icon: "link", iconSet: "heroicons" }],
	[ConversionType.FILE_DOWNLOADED, { icon: "document", iconSet: "heroicons" }],
	[ConversionType.POLL_COMPLETED, { icon: "chart-bar-square", iconSet: "heroicons" }],
]);

export const assetIconProvider: Map<AssetType, IconAsset> = new Map([
	[AssetType.CODE_BATCH, { icon: "arrow-right-circle", iconSet: "heroicons" }],
	[AssetType.ENTRYPOINT_BATCH, { icon: "arrow-right-circle", iconSet: "heroicons" }],
	[AssetType.ENTRYPOINT_BATCH_REF, { icon: "arrow-right-on-rectangle", iconSet: "heroicons" }],
	[AssetType.CODE_V3, { icon: "qr-code", iconSet: "flowcode" }],
	[AssetType.LANDING_PAGE, { icon: "device-phone-mobile", iconSet: "heroicons" }],
	[AssetType.PAGE_V2, { icon: "code-bracket-square", iconSet: "heroicons" }],
	[AssetType.EXTERNAL_DESTINATION, { icon: "link", iconSet: "heroicons" }],
	[AssetType.MODULE, { icon: "cube", iconSet: "ph" }],
	[AssetType.EXPERIMENT, { icon: "beaker", iconSet: "heroicons" }],
	[AssetType.DECISION, { icon: "arrow-split", iconSet: "humbleicons" }],
	[AssetType.PAGE_CONTAINER, { icon: "window", iconSet: "heroicons" }],
	[AssetType.SUITE_REF, { icon: "arrow-right-on-rectangle", iconSet: "heroicons" }],
	[AssetType.STATIC_ASSET, { icon: "document", iconSet: "heroicons" }],
	[AssetType.POLICY, { icon: "clipboard-document-check", iconSet: "heroicons" }],
	[AssetType.POLICY_REF, { icon: "clipboard-document-check", iconSet: "heroicons" }],
	[AssetType.AGENT, { icon: "rocket-launch", iconSet: "heroicons" }],
	[AssetType.LOOKUP, { icon: "circle-stack", iconSet: "heroicons" }],
	[AssetType.EXIT, { icon: "stop-circle", iconSet: "heroicons" }],
	[AssetType.AD_UNIT, { icon: "megaphone", iconSet: "heroicons" }],
	[AssetType.GOTO, { icon: "arrow-path", iconSet: "heroicons" }],
]);

export const providerIcons: Map<Provider, IconAsset> = new Map([
	[Provider.CODE, { icon: "code-braces", iconSet: "mdi" }],
	[Provider.CONTENT, { icon: "content-view-16-regular", iconSet: "fluent" }],
	[Provider.FORM, { icon: "table-cells", iconSet: "heroicons" }],
	[Provider.CONTACT_PROPERTY, { icon: "book-open", iconSet: "heroicons" }],
	[Provider.AGENT, { icon: "rocket-launch", iconSet: "heroicons" }],
	[Provider.EVENT, { icon: "calendar-days", iconSet: "heroicons" }],
	[Provider.LOOKUP, { icon: "circle-stack", iconSet: "heroicons" }],
	[Provider.INVALID, { icon: "exclamation-triangle", iconSet: "heroicons" }],
]);

export const socialIcons: Map<SocialLinkType, IconAsset> = new Map([
	[SocialLinkType.LINKEDIN, { icon: "linkedin", iconSet: "mdi" }],
	[SocialLinkType.X, { icon: "social-x-line", iconSet: "mingcute" }],
	[SocialLinkType.INSTAGRAM, { icon: "instagram", iconSet: "mdi" }],
	[SocialLinkType.THREADS, { icon: "threads-line", iconSet: "mingcute" }],
	[SocialLinkType.FACEBOOK, { icon: "facebook", iconSet: "mdi" }],
	[SocialLinkType.SNAPCHAT, { icon: "snapchat", iconSet: "mdi" }],
	[SocialLinkType.TIKTOK, { icon: "tiktok-line", iconSet: "mingcute" }],
	[SocialLinkType.YOUTUBE, { icon: "youtube", iconSet: "mdi" }],
	[SocialLinkType.SPOTIFY, { icon: "spotify", iconSet: "mdi" }],
	[SocialLinkType.SOUNDCLOUD, { icon: "soundcloud", iconSet: "mdi" }],
	[SocialLinkType.APPLE_MUSIC, { icon: "apple", iconSet: "mdi" }],
	[SocialLinkType.WHATSAPP, { icon: "whatsapp", iconSet: "mdi" }],
	[SocialLinkType.TWITCH, { icon: "twitch", iconSet: "mdi" }],
	[SocialLinkType.DISCORD, { icon: "discord", iconSet: "mdi" }],
]);

function getIconForExternalDestination(url?: string): IconAsset | undefined {
	if (url?.startsWith(mailtoProtocol)) {
		return { icon: "envelope", iconSet: "heroicons" };
	} else if (url?.startsWith(telProtocol)) {
		return { icon: "phone", iconSet: "heroicons" };
	} else if (url?.startsWith(smsProtocol)) {
		return { icon: "chat-bubble-left-ellipsis", iconSet: "heroicons" };
	} else if (url?.startsWith(secureProtocol) || url?.startsWith(insecureProtocol) || (url?.includes("{{") && url?.includes("}}"))) {
		return { icon: "link", iconSet: "heroicons" };
	}
	return { icon: "device-phone-mobile", iconSet: "heroicons" };
}

export function getIconForTemplate<Template extends Bundle | ModuleTemplate>(template: Template): IconAsset | undefined {
	if ("bundleType" in template) {
		// Attempt to determine the destination type based on the URL
		// We can use this to set the icon accordingly
		if (template.bundleType === BundleType.EXTERNAL) {
			return getIconForExternalDestination(template.templates["external_destination-1"]?.defaultConfig?.url as string | undefined);
		}
		return iconProvider.get(template?.bundleType);
	} else if ("entryPoint" in template && template.iconUrl) {
		return parseIconUrl(template.iconUrl);
	}
}
